import { sendGAEvent } from '@next/third-parties/google';

export type EventType =
  | 'feature_click'
  | 'banner_view'
  | 'banner_click'
  | 'form_open'
  | 'form_submit'
  | 'message_open'
  | 'message_dismiss'
  | 'message_click'
  | 'payroll_approve'
  | 'payroll_reject'
  | 'payroll_edit'
  | 'payroll_delete'
  | 'document_download'
  | 'document_view';

export type FeatureEventName =
  | 'add_employee'
  | 'create_contract'
  | 'send_payment'
  | 'create_payroll';

const eventNameKeyByType: Record<EventType, string> = {
  feature_click: 'feature_name',
  form_open: 'form_name',
  form_submit: 'form_name',
  message_open: 'message_name',
  message_dismiss: 'message_name',
  message_click: 'message_name',
  payroll_approve: 'payroll_action',
  payroll_reject: 'payroll_action',
  payroll_edit: 'payroll_action',
  payroll_delete: 'payroll_action',
  document_download: 'document_action',
  document_view: 'document_action',
  banner_view: 'banner_name',
  banner_click: 'banner_name',
};

/**
 * Track a custom event with Google Analytics
 * Uses sendGAEvent from @next/third-parties/google with automatic parameter name mapping
 *
 * @param eventType - The type of event (e.g., 'message_open', 'feature_click')
 * @param name - The name/identifier for the event (automatically mapped to the correct parameter)
 * @param additionalParams - Additional event parameters
 *
 * @example
 * trackEvent('message_open', 'alert_critical', { alert_id: 123 })
 * // Sends: sendGAEvent('event', 'message_open', { message_name: 'alert_critical', alert_id: 123 })
 */
export function trackEvent(
  eventType: EventType,
  name: string,
  additionalParams?: Record<string, unknown>
) {
  const eventNameKey = eventNameKeyByType[eventType] ?? 'event_name';

  const eventData = {
    [eventNameKey]: name,
    ...(additionalParams || {}),
  };

  // Use the new sendGAEvent API signature: sendGAEvent('event', eventName, eventParams)
  sendGAEvent('event', eventType, eventData);
}
